import type { PhotoCandid } from '@/api/types/photo'
import {
  filenameSearchApiTokens,
  getAlbumSearchMode,
  matchesAlbumNormalized,
} from '@/lib/photos/album-search'

/**
 * Image Service indexes the real stored filename (often with extension).
 * Tray `displayName` frequently omits it, so extension search would otherwise
 * drop every valid API hit. Only used when refining API search candidates.
 */
const VIRTUAL_IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'tif',
  'tiff',
] as const

/** Search is active when the normalised term has at least one character. */
export function isFilenameSearchActive(filenameQuery: string): boolean {
  return getAlbumSearchMode(filenameQuery) === 'normalized'
}

/**
 * Longest API-friendly token for Image Service (literal substring match).
 * Strip-chars become word breaks so `st. mary's` → `mary` (not `marys`).
 */
export function getFilenameSearchApiQuery(filenameQuery: string): string | null {
  if (!isFilenameSearchActive(filenameQuery)) return null

  const words = filenameSearchApiTokens(filenameQuery)

  if (words.length === 0) return null

  return words.reduce((longest, word) => (word.length > longest.length ? word : longest), words[0])
}

function matchesWithVirtualExtensions(displayName: string, filenameQuery: string): boolean {
  if (/\.[a-z0-9]{2,5}$/i.test(displayName.trim())) {
    return false
  }

  return VIRTUAL_IMAGE_EXTENSIONS.some((ext) =>
    matchesAlbumNormalized(`${displayName}.${ext}`, filenameQuery)
  )
}

export function matchesFilenameNormalized(
  displayName: string,
  filenameQuery: string,
  options?: { allowVirtualExtensions?: boolean }
): boolean {
  if (matchesAlbumNormalized(displayName, filenameQuery)) return true

  if (options?.allowVirtualExtensions === true) {
    return matchesWithVirtualExtensions(displayName, filenameQuery)
  }

  return false
}

export function mergeFilenameSearchCandidates(
  searchPhotos: PhotoCandid[],
  albumPhotos: PhotoCandid[]
): PhotoCandid[] {
  const byId = new Map<string, PhotoCandid>()

  for (const photo of searchPhotos) {
    byId.set(photo.candidId, photo)
  }

  for (const photo of albumPhotos) {
    if (!byId.has(photo.candidId)) {
      byId.set(photo.candidId, photo)
    }
  }

  return [...byId.values()]
}

/** Drop Image Service hits that are not in the current album tray. */
export function scopeSearchPhotosToAlbum(
  searchPhotos: PhotoCandid[],
  albumPhotos: PhotoCandid[]
): PhotoCandid[] {
  const albumIds = new Set(albumPhotos.map((photo) => photo.candidId))

  return searchPhotos.filter((photo) => albumIds.has(photo.candidId))
}

/**
 * Refine candidates with full normalisation.
 * Pass `allowVirtualExtensions` for Image Service hits so omitted tray extensions
 */
export function filterPhotosByFilenameSearch(
  photos: PhotoCandid[],
  filenameQuery: string,
  options?: { allowVirtualExtensions?: boolean }
): PhotoCandid[] {
  if (!isFilenameSearchActive(filenameQuery)) return photos

  return photos.filter((photo) =>
    matchesFilenameNormalized(photo.displayName, filenameQuery, options)
  )
}

/**
 * Combine API search results with the current album tray, then apply normalised filtering.
 */
export function resolveFilenameSearchPhotos(
  searchPhotos: PhotoCandid[],
  albumPhotos: PhotoCandid[],
  filenameQuery: string,
  options?: { scopeToAlbum?: boolean }
): PhotoCandid[] {
  if (!isFilenameSearchActive(filenameQuery)) return albumPhotos

  const scopedSearchPhotos = options?.scopeToAlbum
    ? scopeSearchPhotosToAlbum(searchPhotos, albumPhotos)
    : searchPhotos
  const fromApi = filterPhotosByFilenameSearch(scopedSearchPhotos, filenameQuery, {
    allowVirtualExtensions: true,
  })
  const fromAlbum = filterPhotosByFilenameSearch(albumPhotos, filenameQuery)

  return mergeFilenameSearchCandidates(fromApi, fromAlbum)
}
